import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor, releaseIdempotencyKey } from '@/lib/http-core';
import { errorFromResponse } from '@/lib/errors';
import type { CollectionMall } from '../../productCollection/collectionMalls';
import type { CommonConsentCode } from '../commonConsents';

/**
 * QA-507 — 공통 동의 문구(catalog QA-479). GET /admin/v1/cms/consent-templates?mall= 은 그 몰의 4종을 주고(저장 전이면 빈 문구),
 * PUT /admin/v1/cms/consent-templates/{code}?mall= 은 한 코드·한 몰을 저장한다. 사용함인 모든 상품의 그 몰 문구가 바뀐다.
 */
export interface ConsentTemplate {
  code: CommonConsentCode;
  mall: string;
  title: string;
  contentHtml: string;
  required: boolean;
  updatedAt: string | null;
}

export interface ConsentTemplateInput {
  title: string;
  contentHtml: string;
  required: boolean;
}

const BASE = '/admin/v1/cms/consent-templates';

export const consentTemplatesQueryKey = (mall: CollectionMall) => ['admin', 'consent-templates', mall] as const;

export const useConsentTemplatesQuery = (mall: CollectionMall) =>
  useQuery<ConsentTemplate[]>({
    queryKey: consentTemplatesQueryKey(mall),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}?mall=${encodeURIComponent(mall)}`);
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as { data?: ConsentTemplate[] | null };
      return env.data ?? [];
    },
    staleTime: 60 * 1000,
  });

export const useSaveConsentTemplateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ code, mall, input }: { code: CommonConsentCode; mall: CollectionMall; input: ConsentTemplateInput }) => {
      const path = `${BASE}/${encodeURIComponent(code)}?mall=${encodeURIComponent(mall)}`;
      const body = { title: input.title.trim(), contentHtml: input.contentHtml, required: input.required };
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await errorFromResponse(res);
      // 같은 문구로 되돌려 저장하는 경우가 첫 응답 재생으로 묻히지 않게 키를 푼다.
      releaseIdempotencyKey('PUT', path, body);
    },
    onSuccess: (_, { mall }) => qc.invalidateQueries({ queryKey: consentTemplatesQueryKey(mall) }),
  });
};
